import {memo, useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Icon} from './Icon.jsx';
import {api, jsonOptions} from '../api/client.js';
import {useAuth} from '../store/AuthContext.jsx';
import {useCart} from '../store/CartContext.jsx';

const compareKey = 'lehaq-compare-v1';
const readCompare = () => { try { const list = JSON.parse(localStorage.getItem(compareKey)); return Array.isArray(list) ? list : []; } catch { return []; } };

const pad = value => String(value).padStart(2, '0');
function Countdown({until}) {
  const target = new Date(until).getTime();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);
  const diff = target - now;
  if (!Number.isFinite(target) || diff <= 0) return null;
  const days = Math.floor(diff / 86400000), hours = Math.floor(diff / 3600000) % 24,
    minutes = Math.floor(diff / 60000) % 60, seconds = Math.floor(diff / 1000) % 60;
  return <span className="sg-countdown" aria-label="Deal ends in">
    <i>{pad(days)}d</i><i>{pad(hours)}h</i><i>{pad(minutes)}m</i><i>{pad(seconds)}s</i>
  </span>;
}

export const ProductCard = memo(function ProductCard({product, compact = false}) {
  const {add} = useCart(), {authenticated} = useAuth(), navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [adding, setAdding] = useState(false);
  const [cartMessage, setCartMessage] = useState('');
  async function addToCart() {
    if (adding) return;
    setAdding(true); setCartMessage('');
    try { await add(product); setCartMessage('Added to your cart'); }
    catch (error) { setCartMessage(error.message || 'Could not add this item. Please try again.'); }
    finally { setAdding(false); }
  }
  const [comparing, setComparing] = useState(() => readCompare().includes(product.slug));
  useEffect(() => {
    const sync = () => setComparing(readCompare().includes(product.slug));
    window.addEventListener('lehaq:compare-changed', sync);
    return () => window.removeEventListener('lehaq:compare-changed', sync);
  }, [product.slug]);
  const toggleWish = async event => {
    event.preventDefault();
    if (!authenticated) { navigate('/account'); return; }
    try { await api(`/wishlist/${product.id}`, jsonOptions('PUT')); setSaved(value => !value); } catch {/* keep card interactive on failure */}
  };
  const toggleCompare = () => {
    let list = readCompare();
    list = list.includes(product.slug) ? list.filter(slug => slug !== product.slug) : [...list, product.slug];
    while (list.length > 4) list.shift();
    localStorage.setItem(compareKey, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent('lehaq:compare-changed'));
  };
  const price = Number(product.pricing?.price ?? product.price ?? 0),
    compare = Number(product.pricing?.compare_at ?? product.compare_at_price ?? 0),
    discount = compare > price ? Math.round((1 - price / compare) * 100) : 0;
  return <article className={`product-card sg-product ${compact ? 'compact' : ''}`}>
    <Link className="product-media product-thumb" to={`/products/${product.slug}`}>
      {discount ? <span className="sale base">{discount}% OFF</span> : null}
      <img src={product.image || '/design-assets/image.png'} alt={product.name} loading="lazy" width="320" height="320"/>
      {discount && product.sale_ends_at ? <Countdown until={product.sale_ends_at}/> : null}
    </Link>
    <button className={`icon-action wish-fixed ${saved ? 'is-active' : ''}`} aria-label={`Save ${product.name}`} aria-pressed={saved} onClick={toggleWish}><Icon name="heart"/></button>
    <div className="icons">
      <button className={`icon-action ${comparing ? 'is-active' : ''}`} aria-pressed={comparing} title={comparing ? 'Remove from compare' : 'Add to compare'} aria-label={`Compare ${product.name}`} onClick={toggleCompare}>⇄</button>
    </div>
    <div className="product-copy product-info">
      <span className="price">{discount ? <s>৳{compare.toLocaleString()}</s> : null}<strong>৳{price.toLocaleString()}</strong></span>
      <Link className="product-name" to={`/products/${product.slug}`}>{product.name}</Link>
      <div className="rating sg-rating"><span className="stars">★★★★★</span> <i>({Number(product.rating ?? 0).toFixed(1)})</i></div>
      <button className="card-add" disabled={adding} onClick={addToCart} aria-label={`Add ${product.name} to cart`}><Icon name="bag"/>{adding ? 'Adding...' : 'Add to cart'}</button>
      {cartMessage ? <span className="card-feedback" role="status">{cartMessage}</span> : null}
    </div>
  </article>;
});
